import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneInput } from './PhoneInput';
import { AllergyInput } from './AllergyInput';
import apiClient from '../lib/axios';

interface PatientAllergy {
  id: string;
  rxcui: string | null;
  name: string;
  addedByDoctor: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface Patient {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  dateOfBirth?: string;
  allergies?: PatientAllergy[];
  chronicConditions?: string;
  insuranceCompanyId?: string;
  insuranceNumber?: string;
  generalNotes?: string;
  shareWithOtherDoctors: boolean;
}

interface PatientEditDialogProps {
  patient: Patient;
  open: boolean;
  onClose: () => void;
}

interface InsuranceCompany {
  id: string;
  name: string;
}

const patientSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  dateOfBirth: z.string().optional(),
  chronicConditions: z.string().optional(),
  insuranceCompanyId: z.string().optional(),
  insuranceNumber: z.string().optional(),
  generalNotes: z.string().optional(),
  shareWithOtherDoctors: z.boolean(),
});

type PatientFormData = z.infer<typeof patientSchema>;

export const PatientEditDialog: React.FC<PatientEditDialogProps> = ({
  patient,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: insuranceCompanies = [], isLoading: isLoadingInsurance } = useQuery({
    queryKey: ['insurance-companies'],
    queryFn: async () => {
      const { data } = await apiClient.get<InsuranceCompany[]>('/insurance-companies');
      return data;
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<PatientFormData>({
    resolver: zodResolver(patientSchema),
    defaultValues: {
      name: patient.name,
      phone: patient.phone || '',
      email: patient.email || '',
      dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.split('T')[0] : '',
      chronicConditions: patient.chronicConditions || '',
      insuranceCompanyId: patient.insuranceCompanyId || '',
      insuranceNumber: patient.insuranceNumber || '',
      generalNotes: patient.generalNotes || '',
      shareWithOtherDoctors: patient.shareWithOtherDoctors,
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: PatientFormData) => {
      // Clean empty strings from optional fields
      const cleanedData = {
        ...data,
        phone: data.phone || undefined,
        email: data.email || undefined,
        dateOfBirth: data.dateOfBirth || undefined,
        chronicConditions: data.chronicConditions || undefined,
        insuranceCompanyId: data.insuranceCompanyId || undefined,
        insuranceNumber: data.insuranceNumber || undefined,
        generalNotes: data.generalNotes || undefined,
      };
      await apiClient.put(`/patients/${patient.id}`, cleanedData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient', patient.id] });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      onClose();
    },
  });

  const onSubmit = (data: PatientFormData) => {
    updateMutation.mutate(data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Patient Information</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {updateMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to update patient. Please try again.
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Full Name"
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    value={field.value || ''}
                    onChange={field.onChange}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            {/* Date of Birth */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('patients.dateOfBirth')}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.dateOfBirth}
                    helperText={errors.dateOfBirth?.message}
                  />
                )}
              />
            </Grid>

            {/* Allergies */}
            <Grid item xs={12}>
              <AllergyInput
                patientId={patient.id}
                allergies={patient.allergies || []}
                disabled={updateMutation.isPending}
              />
            </Grid>

            {/* Chronic Conditions */}
            <Grid item xs={12}>
              <Controller
                name="chronicConditions"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Chronic Conditions"
                    multiline
                    rows={2}
                    placeholder="e.g., Diabetes, Hypertension"
                    error={!!errors.chronicConditions}
                    helperText={errors.chronicConditions?.message}
                  />
                )}
              />
            </Grid>

            {/* Insurance Company */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="insuranceCompanyId"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={insuranceCompanies}
                    getOptionLabel={(option) => option.name}
                    value={insuranceCompanies.find((c) => c.id === field.value) || null}
                    onChange={(_, newValue) => {
                      field.onChange(newValue?.id || '');
                    }}
                    loading={isLoadingInsurance}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Insurance Company"
                        placeholder="Select insurance company"
                        error={!!errors.insuranceCompanyId}
                        helperText={errors.insuranceCompanyId?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoadingInsurance ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Insurance Number */}
            <Grid item xs={12} sm={6}>
              <Controller
                name="insuranceNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Insurance Number"
                    placeholder="Enter policy/member number"
                    error={!!errors.insuranceNumber}
                    helperText={errors.insuranceNumber?.message}
                  />
                )}
              />
            </Grid>

            {/* General Notes */}
            <Grid item xs={12}>
              <Controller
                name="generalNotes"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="General Notes"
                    multiline
                    rows={3}
                    placeholder="Any additional notes about the patient..."
                    error={!!errors.generalNotes}
                    helperText={errors.generalNotes?.message}
                  />
                )}
              />
            </Grid>

            {/* Share With Other Doctors */}
            <Grid item xs={12}>
              <Controller
                name="shareWithOtherDoctors"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          Share clinical data with patient's other doctors
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Allow other doctors treating this patient to see allergies, medications, and chronic conditions (recommended for patient safety)
                        </Typography>
                      </Box>
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
